import ToiTeaserComponents from '@components/Teaser/Toi'
import config from '@config'
import contextToiTeaser from '@contexts/toiTeaser'
import { UseViewportTracking } from '@hooks/useViewportTracking'
import { FunctionComponent, useContext } from 'react'
import SportsHeader from '../../sportsHeader'
import { ImageCrops, TeaserVideo } from '../../types'
import Sporttip from '../Sporttip'
import TeaserImage from '../TeaserImage'
import TeaserLogo from '../TeaserLogo'
import TeaserSportsTiles from '../TeaserSportsTiles'
import TeaserVideoInfo from '../TeaserVideoInfo'
import TOITeaserNewstickerContextProvider from '../TOITeaserNewstickerContextProvider'
import { TOITeaserAPIProps } from '../types'

const {
  publication: { publication },
} = config

interface DefaultTOILayoutProps
  extends Pick<
    TOITeaserAPIProps,
    | 'labelText'
    | 'title'
    | 'catchword'
    | 'subtitle'
    | 'promo'
    | 'bulletpoints'
    | 'scribble'
    | 'matchId'
    | 'typeOfSport'
    | 'relatedMatches'
    | 'swisslosUrl'
    | 'logo'
  > {
  clickableProps: Record<string, unknown>
  imageSrc: string
  imageAlt: string
  imageAvailableCrops?: ImageCrops
  video?: TeaserVideo
  viewportRef: ReturnType<UseViewportTracking>
}

const { TOITeaserLayoutContext } = contextToiTeaser

const {
  AppendingContentWrapper,
  BulletPoints,
  Catchword,
  EverythingWrapper,
  ExtraDataWrapper,
  Gradient,
  InnerTextWrapper,
  Label,
  NewstickerHorizontal,
  NewstickerVertical,
  OuterTextWrapper,
  OuterWrapper,
  PromoIndicator,
  PromoLabel,
  Subtitle,
  TextLine,
  TextWrapper,
  Title,
} = ToiTeaserComponents

const DefaultTOILayout: FunctionComponent<DefaultTOILayoutProps> = ({
  labelText,
  title,
  catchword,
  subtitle,
  promo,
  bulletpoints,
  scribble,
  matchId,
  typeOfSport,
  relatedMatches,
  swisslosUrl,
  imageSrc,
  imageAlt,
  imageAvailableCrops,
  clickableProps,
  logo,
  video,
  viewportRef,
}) => {
  const { mainTextAlignment, teaserFlex } = useContext(TOITeaserLayoutContext)
  const hasPromo = promo?.isPromo
  const hasBulletpoints = !!bulletpoints?.length
  const scribbleId = scribble?.id
  const cleanedUpMatchId = matchId?.startsWith('stage')
    ? matchId.substring('stage'.length)
    : matchId
  const hasMatch = cleanedUpMatchId && typeOfSport
  const hasNewsticker = !!scribbleId
  const hasAppendedContent = hasBulletpoints || hasNewsticker

  return (
    <TOITeaserNewstickerContextProvider scribbleId={scribbleId}>
      <EverythingWrapper viewportRef={viewportRef}>
        <OuterWrapper clickableProps={clickableProps}>
          <TeaserImage
            src={imageSrc}
            alt={imageAlt}
            availableCrops={imageAvailableCrops}
          />
          <TextWrapper>
            {cleanedUpMatchId && typeOfSport ? (
              <SportsHeader
                matchId={cleanedUpMatchId}
                typeOfSport={typeOfSport}
                teaserType="toi"
                teaserFlex={teaserFlex}>
                <TeaserLogo logo={logo} />
              </SportsHeader>
            ) : null}
            <OuterTextWrapper hasNewsticker={hasNewsticker}>
              <Gradient
                hideIfSquare={false}
                hasBulletpoints={hasBulletpoints}
              />
              <InnerTextWrapper>
                {promo?.isPromo ? null : (
                  <TextLine
                    hasBulletpoints={hasBulletpoints}
                    hasNewsticker={hasNewsticker}
                    overflowNative="mainText"
                  />
                )}
                {labelText && <Label>{labelText}</Label>}
                {catchword ? <Catchword>{catchword}</Catchword> : null}
                <Title>{title}</Title>
                {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
                {bulletpoints?.length ? (
                  <BulletPoints
                    bulletPoints={bulletpoints}
                    hideOnMobile={true}
                  />
                ) : null}
              </InnerTextWrapper>
            </OuterTextWrapper>
            {(promo?.isPromo && mainTextAlignment.h === 'left') ||
            (!!logo?.src &&
              mainTextAlignment.h === 'left' &&
              mainTextAlignment.v === 'bottom') ? null : (
              <TeaserVideoInfo
                video={video}
                isStandalone={true}
                isInPromo={!!promo?.isPromo}
              />
            )}
          </TextWrapper>
          {hasAppendedContent ? (
            <AppendingContentWrapper
              hideOnMobile={false}
              extraMargin={hasNewsticker}>
              {bulletpoints?.length ? (
                <>
                  {promo?.isPromo ? null : (
                    <TextLine
                      hasBulletpoints={false}
                      overflowNative="bulletContent"
                    />
                  )}
                  <BulletPoints
                    bulletPoints={bulletpoints}
                    hideOnMobile={false}
                  />
                </>
              ) : null}
              {hasNewsticker ? (
                <>
                  <TextLine
                    hasBulletpoints={false}
                    overflowNative="newsTickerContent"
                  />
                  <NewstickerVertical />
                </>
              ) : null}
            </AppendingContentWrapper>
          ) : null}

          {!hasMatch && !hasPromo && !!logo?.src ? (
            <ExtraDataWrapper hasPromo={false}>
              {mainTextAlignment.h === 'left' &&
              mainTextAlignment.v === 'bottom' ? (
                <TeaserVideoInfo video={video} isStandalone={false} />
              ) : null}
              <TeaserLogo logo={logo} />
            </ExtraDataWrapper>
          ) : null}

          {promo?.isPromo ? (
            <PromoIndicator labelLength={promo.promoLabel.length}>
              <PromoLabel>{promo.promoLabel}</PromoLabel>
              <ExtraDataWrapper hasPromo={true}>
                <TeaserLogo logo={logo} />
                {mainTextAlignment.h === 'left' ? (
                  <TeaserVideoInfo video={video} isStandalone={false} />
                ) : null}
              </ExtraDataWrapper>
            </PromoIndicator>
          ) : null}
        </OuterWrapper>
        {hasNewsticker ? (
          <NewstickerHorizontal clickableProps={clickableProps} />
        ) : null}
        {relatedMatches?.length ? (
          <TeaserSportsTiles tiles={relatedMatches} />
        ) : null}
        {swisslosUrl && publication !== 'romandie' ? (
          <Sporttip url={swisslosUrl} />
        ) : null}
      </EverythingWrapper>
    </TOITeaserNewstickerContextProvider>
  )
}

export default DefaultTOILayout
